<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    const { signOut, loggedIn } = useAuth()

    const { $listen, $unlisten } = useNuxtApp();

    import { useWindowStore } from '@/stores/window';
    import { useHelpersStore } from '@/stores/helpers';
    import { useUserStore } from '@/stores/user';
    import { useArchivesStore } from '@/stores/archives';
    import { useArchivesNeighborhoodsStore } from '@/stores/archives-neighborhoods';

    import { updateHead } from '@/composables/helpers/updateHead';
    // Was `checkTime()` from the now-deleted app/composables/fetchIntervals.ts (Task 10).
    const REFRESH_GATE_SECONDS = 45;
    const checkTime = (storeVal, currentVal) => Math.floor((currentVal - storeVal) / 1000) >= REFRESH_GATE_SECONDS;

    const frontEnd = useRuntimeConfig().public.clientFrontend;
    const route = useRoute();

    updateHead({
        title: 'Open House Chicago Archives | Chicago Architecture Center',
        description:
            "The Chicago Architecture Center's Open House Chicago is a FREE annual festival that typically offers access to hundreds of sites across Chicago, from iconic locations to under-the-radar architectural gems.",
        path: useRoute().path,
        image: frontEnd + '/img/ohc-share.jpg',
    });

    // Instantiate the stores
    const userStore = useUserStore();
    const archiveStore = useArchivesStore();
    const archiveNeighborhoodStore = useArchivesNeighborhoodsStore();
    const helpersStore = useHelpersStore();
    const windowStore = useWindowStore();

    const year = ref(false);

    archiveStore.fetchArchives();
    archiveNeighborhoodStore.fetchArchivedNeighborhoods();

    helpersStore.lastFetch = Date.now();

    // Variables
    const { readNotifications, hasVisitedBefore, initialized } = storeToRefs(userStore);
    let notifications = [];
    const addToItineraryIsOpen = ref(false);
    const item = ref(null);
    const siteNameForAlert = ref(null);
    const itineraryNameForAlert = ref(null);
    const showNotifications = ref(false);
    const hasUnreadNotifications = ref(false);
    const ohcPages = ['/neighborhoods', '/sites', '/itineraries', '/events', '/my-ohc', '/archive', '/about-ohc'];
    const siteAddedAlertIsOpen = ref(false);
    const itineraryAddedWithSiteAlertIsOpen = ref(false);

    // Methods
    // The notifications endpoint returns { data: [...] }; the old `.data.records`
    // read was always undefined. Caught, because the endpoint now answers 503
    // when KV is unavailable and this runs un-awaited on mount.
    const fetchNotifications = async () => {
        try {
            const notificationsData = await $fetch(`/api/notifications`);
            notifications = notificationsData?.data ?? [];
            getOrSetNotifications();
        } catch (e) {
            console.log('Fetch Notifications Error: ', e);
        }
    };

    function isCACPage() {
        let isCACPage = true;

        let { path } = route;
        if (path.endsWith('/')) path = path.slice(0, -1);

        // We assume that sub-pages of the OHC pages are also OHC pages
        if (path == '') isCACPage = false;
        if (ohcPages.some((page) => path.startsWith(page))) {
            isCACPage = false;
        }

        return isCACPage;
    }

    function setSiteAndItineraryNames(values) {
        siteNameForAlert.value = values?.item?.fields?.Name || 'Site Name';
        console.log(`🍤 ~ setSiteAndItineraryNames ~ values:`, values);
        itineraryNameForAlert.value = values?.selected?.name || 'Itinerary Name';
    }
    function openSiteAddedAlert(values) {
        siteAddedAlertIsOpen.value = true;
        setSiteAndItineraryNames(values);
    }
    function closeSiteAddedAlert() {
        siteAddedAlertIsOpen.value = false;
        siteNameForAlert.value = null;
        itineraryNameForAlert.value = null;
    }

    function openItineraryAddedWithSiteAlert(values) {
        addToItineraryIsOpen.value = false;
        itineraryAddedWithSiteAlertIsOpen.value = true;

        itineraryNameForAlert.value = values.itineraryName;
        siteNameForAlert.value = values.siteName;
    }
    function closeItineraryAddedWithSiteAlert() {
        itineraryAddedWithSiteAlertIsOpen.value = false;
        siteNameForAlert.value = null;
        itineraryNameForAlert.value = null;
    }

    function getOrSetNotifications() {
        if (notifications?.length > readNotifications.value) {
            hasUnreadNotifications.value = true;
        }
    }

    function closeEmailSignupModal(type) {
        userStore.updateHasVisitedBefore(true);
    }

    const resize = () => {
        windowStore.windowHeight = window.innerHeight;
        windowStore.windowWidth = window.innerWidth;
    };

    // Lifecycle
    year.value = false;

    const handleOpenGlobalItineraryModal = (val) => {
        item.value = val.value;
        addToItineraryIsOpen.value = true;
        document.body.style.overflow = 'hidden';
    };

    const handleCloseGlobalItineraryModal = () => {
        addToItineraryIsOpen.value = false;
        document.body.style.overflow = 'auto';
    };

    const handleAddedToItineraryAlert = (val2) => {
        openSiteAddedAlert(val2);
    };

    const handleCreatedItineraryWithSite = (val3) => {
        openItineraryAddedWithSiteAlert(val3);
        document.body.style.overflow = 'auto';
    };

    const handleShowNotifications = () => {
        showNotifications.value = true;
        document.body.style.overflow = 'hidden';
    };

    const handleHideNotifications = () => {
        hasUnreadNotifications.value = false;
        showNotifications.value = false;
        userStore.markNotificationsAsRead(notifications?.length | 0);
        hasUnreadNotifications.value = false;
        document.body.style.overflow = 'auto';
    };

    const busHandlers = [
        ['openGlobalItineraryModal', handleOpenGlobalItineraryModal],
        ['closeGlobalItineraryModal', handleCloseGlobalItineraryModal],
        ['addedToItineraryAlert', handleAddedToItineraryAlert],
        ['createdItineraryWithSite', handleCreatedItineraryWithSite],
        ['showNotifications', handleShowNotifications],
        ['hideNotifications', handleHideNotifications],
    ];

    onMounted(() => {
        fetchNotifications();
        userStore.initUserData();

        if (import.meta.client) {
            resize();
            window.addEventListener('resize', resize);

            for (const [event, handler] of busHandlers) $listen(event, handler);
        }
    });

    // See layouts/default.vue: a layout unmounts when the app swaps to another
    // one, and mitt keeps a subscription until it is explicitly removed, so
    // every swap stacked another copy of all of these.
    onUnmounted(() => {
        if (!import.meta.client) return;
        window.removeEventListener('resize', resize);
        for (const [event, handler] of busHandlers) $unlisten(event, handler);
    });

    watch(
        () => route.path,
        async (to, from) => {
            const canRefetch = checkTime(helpersStore.lastFetch, Date.now());
            if (canRefetch) {
                helpersStore.lastFetch = Date.now();
            }
            fetchNotifications();
        },
        { deep: true }
    );
</script>

<template>
    <div>
        <GlobalOfflineBar />
        <div v-if="route && isCACPage()">
            <Header :isStatic="true" :hasUnreadNotifications="hasUnreadNotifications" />
        </div>
        <div v-else>
            <Header :isStatic="false" :hasUnreadNotifications="hasUnreadNotifications" />
        </div>

        <Notifications v-if="showNotifications && notifications?.length" :notifications="notifications" />
        <GlobalPwaUpdateToast />
        <div v-if="route && isCACPage()">
            <main id="main" tabindex="-1">
                <NuxtPage :page-key="(route) => route.fullPath" class="page" />
            </main>

            <GlobalSiteFooter />
            <BlocksEmailSignupAlert v-if="!hasVisitedBefore && initialized" @closeEmailModal="closeEmailSignupModal" />
            <!-- REMOVED POST-OHC -->
            <!-- <OHCBottomNavigation /> -->
            <!--BottomNavigation /-->
        </div>

        <div v-else>
            <main id="main" tabindex="-1">
                <NuxtPage :page-key="(route) => route.fullPath" class="page" />
            </main>
            <GlobalSiteFooter />
            <GlobalAddToItinerary v-if="addToItineraryIsOpen" :item="item" />

            <ItinerariesAddedSiteAlert v-if="siteAddedAlertIsOpen" @closeAddedSiteAlert="closeSiteAddedAlert" :siteName="siteNameForAlert" :listName="itineraryNameForAlert" />

            <GlobalAddedItineraryWithSiteAlert
                v-if="itineraryAddedWithSiteAlertIsOpen"
                @closeAddedItineraryWithSite="closeItineraryAddedWithSiteAlert"
                :siteName="siteNameForAlert"
                :listName="itineraryNameForAlert" />

            <BlocksEmailSignupAlert v-if="!hasVisitedBefore && initialized" @closeEmailModal="closeEmailSignupModal" />
        </div>
    </div>
</template>

<style lang="scss">
    @use '@/assets/styles/main.scss';

    // The fixed header's height, and the clearance #main needs under it.
    //
    // These were only ever declared in layouts/default.vue, in an UNSCOPED
    // style block. In dev that is invisible: Vite serves every layout's CSS
    // regardless of which layout rendered, so archive pages picked up
    // default.vue's :root block and looked correct. The production build ships
    // CSS per route, so on /archive the variables resolved to nothing —
    // header.vue fell back to its 59px default, #main got `margin-top: var(...)`
    // with no value, and the page heading rendered underneath the fixed header.
    // Dev could not have shown this; only a production build can.
    //
    // Deliberately NOT a copy of default.vue's --sticky-bar-height sum. That
    // layout also renders NavDesktop and NavChips and has to clear all three;
    // this one renders <Header> alone (no teal nav row, no mobile chip bar), so
    // the header's own height IS the whole of the fixed chrome. Copying the sum
    // would have left 62px of dead space below the header — which is what dev
    // was showing, and why the gap there looked plausible rather than wrong.
    :root {
        --header-height: 59px;

        @media (min-width: $desktop) {
            --header-height: 88px;
        }
    }

    #main {
        margin-top: var(--header-height);
    }
</style>
